import { EFinancingProgram } from '@if/core/types'
import { ActionButton, type ActionButtonProps, Icon, ActionMenuItem, useCan } from '@if/mui'
import { access } from '@src/access'
import { IconButton, IconButtonProps, Menu, Tooltip } from '@mui/material'
import { Plus } from 'lucide-react'
import { memo, MouseEventHandler, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  supportedFinancingPrograms: EFinancingProgram[]
}

// Shared new-application routing so the dashboard sidebar and this button/menu
// agree on where each program goes. Auto (and IfXpress, unless the
// UseIFXFinancingProgram flag is on) is still handled by the legacy monolith.
const monolithBaseUrl = (): string => {
  const currentUrl = window.location.href
  if (currentUrl.includes('localhost') || currentUrl.includes('dev')) return 'https://dev01.icebergfinance.ca'
  if (currentUrl.includes('int') || currentUrl.includes('uat')) return 'https://tests.icebergfinance.ca'
  return 'https://secure.icebergfinance.ca'
}

export const isMonolithProgram = (program: EFinancingProgram, useIFXFinancingProgram: boolean): boolean => {
  const monolithPrograms = useIFXFinancingProgram
    ? [EFinancingProgram.Auto]
    : [EFinancingProgram.Auto, EFinancingProgram.IfXpress]
  return monolithPrograms.includes(program)
}

export const getNewApplicationHref = (program: EFinancingProgram, useIFXFinancingProgram: boolean): string =>
  isMonolithProgram(program, useIFXFinancingProgram) ? monolithBaseUrl() : `/credits/${program}/add`

const AddApplicationButtonBase = ({ supportedFinancingPrograms, children, ...props }: Props & ActionButtonProps) => {
  //Feature switch
  const useIFXFinancingProgram = useCan(access.ifxFinancingProgram)

  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const menuId = useId()
  const btnId = useId()

  if (supportedFinancingPrograms.length === 0) return null

  if (supportedFinancingPrograms.length === 1) {
    const program = supportedFinancingPrograms[0]
    return (
      <ActionButton
        emphasis="principal"
        startIcon={Plus}
        href={getNewApplicationHref(program, useIFXFinancingProgram)}
        {...props}
      >
        {children ?? t('common.newApplication')}
      </ActionButton>
    )
  }

  const open = Boolean(anchorEl)

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  return (
    <>
      <ActionButton
        id={btnId}
        aria-controls={menuId}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        emphasis="principal"
        startIcon={Plus}
        {...props}
      >
        {children ?? t('menus.addApplication')}
      </ActionButton>
      <Menu
        id={menuId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': btnId,
          },
        }}
      >
        {supportedFinancingPrograms.map((e) => (
          <ActionMenuItem
            key={e}
            href={getNewApplicationHref(e, useIFXFinancingProgram)}
            target={isMonolithProgram(e, useIFXFinancingProgram) ? '_blank' : undefined}
          >
            {t(`EFinancingProgram.${e}`, { ns: 'enum' })}
          </ActionMenuItem>
        ))}
      </Menu>
    </>
  )
}

export const AddApplicationButton = memo(AddApplicationButtonBase)

const AddApplicationIconButtonBase = ({ supportedFinancingPrograms, ...props }: Props & IconButtonProps) => {
  //Feature switch
  const useIFXFinancingProgram = useCan(access.ifxFinancingProgram)

  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
  const menuId = useId()
  const btnId = useId()

  if (supportedFinancingPrograms.length === 0) return null

  if (supportedFinancingPrograms.length === 1) {
    const program = supportedFinancingPrograms[0]
    return (
      <Tooltip title={t('menus.addApplication')}>
        <IconButton href={getNewApplicationHref(program, useIFXFinancingProgram)} {...props}>
          <Icon icon={Plus} />
        </IconButton>
      </Tooltip>
    )
  }

  const open = Boolean(anchorEl)

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }
  return (
    <>
      <Tooltip title={t('menus.addApplication')}>
        <IconButton
          id={btnId}
          aria-controls={menuId}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          {...props}
        >
          <Icon icon={Plus} />
        </IconButton>
      </Tooltip>
      <Menu
        id={menuId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        slotProps={{
          list: {
            'aria-labelledby': btnId,
          },
        }}
      >
        {supportedFinancingPrograms.map((e) => (
          <ActionMenuItem
            key={e}
            href={getNewApplicationHref(e, useIFXFinancingProgram)}
            target={isMonolithProgram(e, useIFXFinancingProgram) ? '_blank' : undefined}
          >
            {t(`EFinancingProgram.${e}`, { ns: 'enum' })}
          </ActionMenuItem>
        ))}
      </Menu>
    </>
  )
}

export const AddApplicationIconButton = memo(AddApplicationIconButtonBase)
